import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import TierDiscountStrip from "../components/TierDiscountStrip";
import SEO, { LOCAL_BUSINESS_JSONLD } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LehengaHireMelbourne() {
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/outfits?category=Lehengas`)
      .then(r => r.json())
      .then(d => setOutfits(d.filter(o => o.available).slice(0, 12)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <SEO
        rawTitle="Lehenga Hire Melbourne – Rent Bridal & Designer Lehengas | Mahra"
        description="Hire premium lehengas in Melbourne for weddings, sangeets and receptions. Bridal and party lehengas available for rent with free local pickup. From $89/rental."
        path="/lehenga-hire-melbourne"
        jsonLd={LOCAL_BUSINESS_JSONLD}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Lehenga Hire Melbourne" },
        ]}
      />
      <Navbar />
      <TierDiscountStrip />

      {/* Hero Section */}
      <section className="bg-white border-b border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-[12px] mb-6" data-testid="seo-breadcrumb">
            <Link to="/" className="text-[#676664]/50 hover:text-[#676664] transition-colors">Home</Link>
            <span className="text-[#676664]/30">/</span>
            <Link to="/indian-outfit-hire-melbourne" className="text-[#676664]/50 hover:text-[#676664] transition-colors">Indian Outfit Hire</Link>
            <span className="text-[#676664]/30">/</span>
            <span className="text-[#282828] font-medium">Lehenga Hire Melbourne</span>
          </nav>
          <h1 className="text-3xl lg:text-5xl text-[#282828] font-light leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="seo-page-h1">
            Lehenga Hire <span className="italic">in Melbourne</span>
          </h1>
          <p className="text-[14px] text-[#676664] leading-relaxed max-w-2xl">
            Rent stunning bridal and premium lehengas in Melbourne. From intricate hand-embroidered wedding pieces to vibrant sangeet styles, find your perfect lehenga without the permanent price tag.
          </p>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-12 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10">
          <h2 className="text-xl lg:text-2xl text-[#282828] font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Lehengas Available for Hire</h2>
          <p className="text-[13px] text-[#676664] mb-8">Explore our range of Indian outfits available for rent in Melbourne.</p>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {[...Array(8)].map((_, i) => <div key={i} className="bg-[#E8E0D5] animate-pulse" style={{ paddingBottom: '133%' }} />)}
            </div>
          ) : outfits.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="seo-product-grid">
              {outfits.map(outfit => (
                <ProductCard key={outfit.id} outfit={outfit} />
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-[#676664]">Our lehenga collection is being updated. Please check back soon or <Link to="/catalog" className="text-[#9A7B4F] hover:underline">browse our full collection</Link>.</p>
          )}
          <div className="mt-8 text-center">
            <Link to="/catalog" className="inline-flex items-center gap-2 text-[11px] text-[#282828] tracking-[0.2em] uppercase border-b border-[#282828] pb-1 hover:opacity-70 transition-opacity" data-testid="view-all-lehengas-link">
              View All Lehengas <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="border-t border-[#282828]/5 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-14 lg:py-20">
          <div className="max-w-3xl">
            <h2 className="text-xl lg:text-2xl text-[#282828] font-light mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Rent a Lehenga in Melbourne</h2>
            <div className="text-[13px] text-[#676664] leading-relaxed space-y-4">
              <p>A lehenga is more than an outfit — it's a statement. Whether you're the bride, a bridesmaid, or a guest at a Melbourne wedding, the right lehenga transforms how you feel and how others see you. At Mahra, we make it possible to wear breathtaking designer lehengas without the permanent investment.</p>

              <p><strong className="text-[#282828] font-medium">Bridal Lehengas for Hire</strong></p>
              <p>Our bridal collection features exquisitely crafted lehengas with heavy embroidery, zardozi work, and premium fabrics. Each piece has been carefully selected to meet the expectations of Melbourne brides who want tradition with a touch of modern elegance. Full sets include the lehenga skirt, matching blouse, and dupatta — everything you need for your special day.</p>

              <p><strong className="text-[#282828] font-medium">Sangeet, Reception & Party Lehengas</strong></p>
              <p>Not every event calls for heavy bridal wear. Our collection includes lighter, more playful lehenga styles perfect for sangeet nights, receptions, and festive celebrations. Bold colours, contemporary silhouettes, and intricate detailing make each piece a standout choice for any Melbourne event.</p>

              <p><strong className="text-[#282828] font-medium">Flexible Rental Options</strong></p>
              <p>Choose from our 4-day or 8-day rental periods to suit your event schedule. Need it for a destination wedding weekend? The 8-day option gives you extra time without the stress. Each lehenga arrives freshly dry-cleaned, pressed, and ready to wear.</p>

              <p><strong className="text-[#282828] font-medium">Complete Your Bridal Look</strong></p>
              <p>Pair your lehenga with coordinating jewellery and a designer clutch through our <Link to="/build-your-look" className="text-[#9A7B4F] hover:underline">Build Your Look</Link> feature. Hire multiple pieces together and enjoy bundle discounts of up to 20% — making your complete bridal ensemble more accessible than ever.</p>

              <p>Explore our full range of <Link to="/indian-outfit-hire-melbourne" className="text-[#9A7B4F] hover:underline">premium Indian outfits for hire in Melbourne</Link>, or discover our curated <Link to="/saree-rental-melbourne" className="text-[#9A7B4F] hover:underline">saree rental collection</Link> for a different look.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="border-t border-[#282828]/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-10">
          <p className="text-[10px] text-[#676664] tracking-[0.3em] uppercase mb-4">Explore More</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/indian-outfit-hire-melbourne" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-indian">Indian Outfit Hire Melbourne</Link>
            <Link to="/saree-rental-melbourne" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-saree">Saree Rental Melbourne</Link>
            <Link to="/looks" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-looks">Curated Looks</Link>
            <Link to="/build-your-look" className="text-[12px] text-[#282828] border border-[#282828]/15 px-4 py-2 hover:bg-[#282828] hover:text-white transition-colors" data-testid="cross-link-build">Build Your Look</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
